import LazyApexChart from "../../client/components/LazyApexChart";
import useTranslation from "../../client/hooks/useTranslation";
import { useIsDark, useThemeId } from "../../client/components/ui/card-section-header";
import { chartColors } from "../../dashboard/widgets/chart/chartColors";
import { useLiteData } from "../data/LiteDataProvider";
import { toneColor, type Tone } from "./ui";

// Graphiques du Lite, sur ApexCharts comme le reste de l'app. Les couleurs sont
// lues dans les variables du thème actif (chartColors) : les 5 thèmes suivent.

function useBase() {
  const isDark = useIsDark();
  const themeId = useThemeId();
  const { fmt } = useLiteData();
  const c = chartColors();
  const axis = { style: { colors: c.axis, fontSize: "11px" } };
  const base = {
    chart: { toolbar: { show: false }, background: "transparent", foreColor: c.axis, fontFamily: "'Red Hat Text', sans-serif", zoom: { enabled: false } },
    grid: { borderColor: c.grid, strokeDashArray: 4, xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } }, padding: { left: 4, right: 4 } },
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: { theme: isDark ? "dark" : "light" },
  };
  return { isDark, themeId, fmt, c, axis, base };
}

export function EquityChart({ data, height = 220 }: { data: { label: string; equity: number }[]; height?: number }) {
  const { themeId, fmt, c, axis, base } = useBase();
  const last = data.at(-1)?.equity ?? 0;
  const color = last >= 0 ? c.positive : c.negative;
  return (
    <LazyApexChart
      key={`eq-${themeId}-${data.length}`}
      type="area"
      height={height}
      series={[{ name: "P&L", data: data.map(d => d.equity) }]}
      options={{
        ...base,
        colors: [color],
        stroke: { curve: "smooth", width: 2 },
        fill: { type: "gradient", gradient: { opacityFrom: 0.28, opacityTo: 0, stops: [0, 100] } },
        xaxis: { categories: data.map(d => d.label), labels: { ...axis, rotate: 0, hideOverlappingLabels: true }, tickAmount: 8, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { ...axis, formatter: (v: number) => fmt.moneyShort(v) } },
        tooltip: { ...base.tooltip, y: { formatter: (v: number) => fmt.money(v) } },
        annotations: { yaxis: [{ y: 0, borderColor: c.grid }] },
      }}
    />
  );
}

export function PnlBars({ data, height = 200 }: { data: { label: string; value: number }[]; height?: number }) {
  const { themeId, fmt, c, axis, base } = useBase();
  return (
    <LazyApexChart
      key={`bars-${themeId}-${data.length}`}
      type="bar"
      height={height}
      series={[{ name: "P&L", data: data.map(d => d.value) }]}
      options={{
        ...base,
        colors: data.map(d => (d.value >= 0 ? c.positive : c.negative)),
        plotOptions: { bar: { distributed: true, borderRadius: 4, columnWidth: "55%" } },
        xaxis: { categories: data.map(d => d.label), labels: { ...axis, rotate: 0, hideOverlappingLabels: true }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { ...axis, formatter: (v: number) => fmt.moneyShort(v) } },
        tooltip: { ...base.tooltip, y: { formatter: (v: number) => fmt.money(v) } },
        annotations: { yaxis: [{ y: 0, borderColor: c.grid }] },
      }}
    />
  );
}

/** Barres de comptage (ex. distribution), sans format monétaire. */
export function CountBars({ data, height = 200 }: { data: { label: string; value: number }[]; height?: number }) {
  const { themeId, c, axis, base } = useBase();
  return (
    <LazyApexChart
      key={`count-${themeId}-${data.length}`}
      type="bar"
      height={height}
      series={[{ name: "", data: data.map(d => Math.abs(d.value)) }]}
      options={{
        ...base,
        colors: data.map(d => (d.value >= 0 ? c.positive : c.negative)),
        plotOptions: { bar: { distributed: true, borderRadius: 4, columnWidth: "60%" } },
        xaxis: { categories: data.map(d => d.label), labels: { ...axis, rotate: -45, hideOverlappingLabels: true }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { ...axis, formatter: (v: number) => String(Math.round(v)) } },
      }}
    />
  );
}

/** Mini courbe sans axes. */
export function Sparkline({ values, height = 36 }: { values: number[]; height?: number }) {
  const { themeId, c } = useBase();
  const color = (values.at(-1) ?? 0) >= 0 ? c.positive : c.negative;
  return (
    <LazyApexChart
      key={`spark-${themeId}-${values.length}`}
      type="area"
      height={height}
      series={[{ name: "", data: values }]}
      options={{
        chart: { sparkline: { enabled: true }, animations: { enabled: false } },
        colors: [color],
        stroke: { curve: "smooth", width: 1.5 },
        fill: { opacity: 0.08 },
        tooltip: { enabled: false },
      }}
    />
  );
}

/** Drawdown sous l'eau : distance au plus haut, jour après jour. */
export function UnderwaterChart({ data, height = 90 }: { data: { label: string; drawdown: number }[]; height?: number }) {
  const { themeId, fmt, c, axis, base } = useBase();
  const t = useTranslation().lite.charts;
  return (
    <LazyApexChart
      key={`dd-${themeId}-${data.length}`}
      type="area"
      height={height}
      series={[{ name: t.drawdown, data: data.map(d => d.drawdown) }]}
      options={{
        ...base,
        colors: [c.negative],
        stroke: { curve: "smooth", width: 1.5 },
        fill: { opacity: 0.15, type: "solid" },
        xaxis: { categories: data.map(d => d.label), labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
        // Graphique bas : pas de libellés d'axe (ils se chevauchent), la valeur est au survol.
        yaxis: { max: 0, labels: { show: false } },
        tooltip: { ...base.tooltip, y: { formatter: (v: number) => fmt.money(v) } },
      }}
    />
  );
}

/** Payouts contre dépenses par mois, avec le net cumulé. */
export function CashflowChart({ data, height = 260 }: { data: { label: string; payouts: number; expenses: number; cumulative: number }[]; height?: number }) {
  const { themeId, fmt, c, axis, base } = useBase();
  const t = useTranslation().lite.charts;
  return (
    <LazyApexChart
      key={`cash-${themeId}-${data.length}`}
      type="line"
      height={height}
      series={[
        { name: t.payouts, type: "column", data: data.map(d => d.payouts) },
        { name: t.expenses, type: "column", data: data.map(d => d.expenses) },
        { name: t.cumulative, type: "line", data: data.map(d => d.cumulative) },
      ]}
      options={{
        ...base,
        chart: { ...base.chart, stacked: true },
        colors: [c.positive, c.negative, c.accent],
        stroke: { width: [0, 0, 2.5], curve: "smooth" },
        markers: { size: [0, 0, 4], hover: { sizeOffset: 3 } },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "45%" } },
        // Barres empilées + courbe : ApexCharts décale le curseur vertical (ligne ou
        // bande) d'une demi-barre dès que le mois a des barres. On le retire : le point
        // survolé grossit et l'infobulle reste accrochée au bon mois.
        xaxis: {
          categories: data.map(d => d.label), labels: axis, axisBorder: { show: false }, axisTicks: { show: false },
          tooltip: { enabled: false },
          crosshairs: { show: false },
        },
        yaxis: { labels: { ...axis, formatter: (v: number) => fmt.moneyShort(v) } },
        tooltip: { ...base.tooltip, shared: true, intersect: false, y: { formatter: (v: number) => fmt.money(v) } },
        annotations: { yaxis: [{ y: 0, borderColor: c.grid }] },
      }}
    />
  );
}

/** Taux de réussite glissant, avec la ligne des 50 %. */
export function RollingChart({ data, height = 220 }: { data: { label: string; value: number }[]; height?: number }) {
  const { themeId, fmt, c, axis, base } = useBase();
  return (
    <LazyApexChart
      key={`roll-${themeId}-${data.length}`}
      type="line"
      height={height}
      series={[{ name: "", data: data.map(d => Math.round(d.value * 1000) / 1000) }]}
      options={{
        ...base,
        colors: [c.accent],
        stroke: { curve: "smooth", width: 2 },
        xaxis: { categories: data.map(d => d.label), labels: { ...axis, rotate: 0, hideOverlappingLabels: true }, tickAmount: 6, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { min: 0, max: 1, tickAmount: 4, labels: { ...axis, formatter: (v: number) => fmt.pct(v) } },
        tooltip: { ...base.tooltip, y: { formatter: (v: number) => fmt.pct(v) } },
        annotations: { yaxis: [{ y: 0.5, borderColor: c.neutral, strokeDashArray: 4 }] },
      }}
    />
  );
}

/** Chaque trade = un point : repère les pertes hors norme. */
export function TradeScatter({ data, height = 240 }: { data: { i: number; pnl: number; symbol: string; date: string }[]; height?: number }) {
  const { themeId, fmt, c, axis, base } = useBase();
  const t = useTranslation().lite.charts;
  return (
    <LazyApexChart
      key={`scat-${themeId}-${data.length}`}
      type="scatter"
      height={height}
      series={[
        { name: t.winners, data: data.filter(d => d.pnl >= 0).map(d => [d.i, d.pnl]) },
        { name: t.losers, data: data.filter(d => d.pnl < 0).map(d => [d.i, d.pnl]) },
      ]}
      options={{
        ...base,
        colors: [c.positive, c.negative],
        markers: { size: 4, strokeWidth: 0, fillOpacity: 0.7 },
        xaxis: { type: "numeric", labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false }, tooltip: { enabled: false } },
        yaxis: { labels: { ...axis, formatter: (v: number) => fmt.moneyShort(v) } },
        tooltip: { ...base.tooltip, x: { show: false }, y: { formatter: (v: number) => fmt.money(v) } },
      }}
    />
  );
}

/** Jauge en demi-cercle (0..1). */
export function Gauge({ value, tone = "brand", size = 84 }: { value: number; tone?: Tone; size?: number }) {
  const v = Math.max(0, Math.min(1, value));
  const c = Math.PI * 36;
  return (
    <svg width={size} height={size * 0.58} viewBox="0 0 84 48" aria-hidden>
      <path d="M6 44 A36 36 0 0 1 78 44" fill="none" stroke="hsl(var(--muted))" strokeWidth="8" strokeLinecap="round" />
      <path d="M6 44 A36 36 0 0 1 78 44" fill="none" stroke={toneColor(tone)} strokeWidth="8" strokeLinecap="round"
        strokeDasharray={`${c * v} ${c}`} style={{ transition: "stroke-dasharray .5s" }} />
    </svg>
  );
}

/**
 * Solde du compte jour après jour, avec le solde de départ en repère.
 * Plancher : `floorSeries` (trailing, il suit le pic) ou `floor` (statique).
 */
export function BalanceChart({ data, start, floor, floorSeries, floorLabel, startLabel, height = 240 }: {
  data: { label: string; balance: number }[];
  start: number;
  floor: number | null;
  floorSeries?: number[] | null;
  floorLabel: string;
  startLabel: string;
  height?: number;
}) {
  const { themeId, fmt, c, axis, base } = useBase();
  const last = data.at(-1)?.balance ?? start;
  const color = last >= start ? c.positive : c.negative;
  const values = data.map(d => d.balance);
  const trailing = !!floorSeries?.length;
  // L'axe inclut le plancher : une marge se lit à la distance entre la courbe et lui.
  const lo = Math.min(...values, start, ...(trailing ? floorSeries! : floor !== null ? [floor] : []));
  const hi = Math.max(...values, start);
  const pad = Math.max((hi - lo) * 0.08, 1);
  const label = (text: string, col: string) => ({ text, borderWidth: 0, style: { background: "transparent", color: col, fontSize: "10px" }, position: "right", textAnchor: "end", offsetY: -2 });
  return (
    <LazyApexChart
      key={`bal-${themeId}-${data.length}-${trailing}`}
      type="line"
      height={height}
      series={[
        { name: "", type: "area", data: values },
        ...(trailing ? [{ name: floorLabel, type: "line", data: floorSeries! }] : []),
      ]}
      options={{
        ...base,
        colors: [color, c.negative],
        stroke: { curve: "smooth", width: trailing ? [2, 1.5] : 2, dashArray: trailing ? [0, 5] : 0 },
        fill: { type: trailing ? ["gradient", "solid"] : "gradient", opacity: trailing ? [1, 0] : 1, gradient: { opacityFrom: 0.22, opacityTo: 0, stops: [0, 100] } },
        xaxis: { categories: data.map(d => d.label), labels: { ...axis, rotate: 0, hideOverlappingLabels: true }, tickAmount: 6, axisBorder: { show: false }, axisTicks: { show: false }, tooltip: { enabled: false } },
        yaxis: { min: lo - pad, max: hi + pad, tickAmount: 4, labels: { ...axis, formatter: (v: number) => fmt.money(v, { sign: false }) } },
        tooltip: { ...base.tooltip, shared: true, y: { formatter: (v: number) => fmt.money(v, { sign: false, decimals: true }) } },
        annotations: {
          yaxis: [
            { y: start, borderColor: c.neutral, strokeDashArray: 3, label: label(startLabel, c.axis) },
            ...(!trailing && floor !== null ? [{ y: floor, borderColor: c.negative, strokeDashArray: 5, label: label(floorLabel, c.negative) }] : []),
          ],
        },
      }}
    />
  );
}
